
import React, { useState, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { STORAGE_BUCKETS, validateImageFile } from '@/utils/storage/bucketUtils';
import { Button } from '@/components/ui/button';
import { Upload, X } from 'lucide-react';

interface ImageUploadProps {
  onImageUploaded: (url: string) => void;
  currentImageUrl?: string;
}

const ImageUpload: React.FC<ImageUploadProps> = ({ onImageUploaded, currentImageUrl }) => {
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentImageUrl || null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Set preview URL when currentImageUrl prop changes
  React.useEffect(() => {
    if (currentImageUrl) {
      setPreviewUrl(currentImageUrl);
    }
  }, [currentImageUrl]);

  const checkAdminPermission = async (): Promise<boolean> => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const userEmail = session?.user?.email;
      
      // Only allow the specific admin to upload images
      return userEmail === 'careerguidekerala@gmail.com';
    } catch (error) {
      console.error('Error checking admin permission:', error);
      return false;
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate the file
    const validation = validateImageFile(file);
    if (!validation.valid) {
      toast.error(validation.error);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Check if user has permission to upload
    const hasPermission = await checkAdminPermission();
    if (!hasPermission) {
      toast.error('You do not have permission to upload images');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Create preview
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);

    // Upload the file
    await uploadFile(file);

    // Clean up preview URL
    return () => URL.revokeObjectURL(objectUrl);
  };

  const uploadFile = async (file: File) => {
    try {
      setUploading(true);
      
      // Generate a unique file name with timestamp to avoid conflicts
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2, 15)}_${Date.now()}.${fileExt}`;
      const filePath = `${fileName}`;

      // Upload the file to Supabase Storage
      const { data, error } = await supabase.storage
        .from(STORAGE_BUCKETS.COUNSELOR_IMAGES)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        throw error;
      }

      // Get the public URL for the file
      const { data: { publicUrl } } = supabase.storage
        .from(STORAGE_BUCKETS.COUNSELOR_IMAGES)
        .getPublicUrl(filePath);

      // Notify parent component about successful upload
      onImageUploaded(publicUrl);
      toast.success('Image uploaded successfully');
      
    } catch (error: any) {
      console.error('Error uploading file:', error);
      toast.error(error.message || 'Error uploading image');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const deleteFile = async (url: string) => {
    try {
      // Check if user has permission to delete
      const hasPermission = await checkAdminPermission();
      if (!hasPermission) {
        toast.error('You do not have permission to remove images');
        return false;
      }

      // Extract file path from URL
      const storageUrl = new URL(url);
      const pathSegments = storageUrl.pathname.split('/');
      const bucketIndex = pathSegments.findIndex(segment => 
        segment === STORAGE_BUCKETS.COUNSELOR_IMAGES);
      
      if (bucketIndex === -1 || bucketIndex === pathSegments.length - 1) {
        console.error('Could not parse file path from URL:', url);
        return false;
      }
      
      const filePath = pathSegments.slice(bucketIndex + 1).join('/');
      
      // Delete the file from storage
      const { error } = await supabase.storage
        .from(STORAGE_BUCKETS.COUNSELOR_IMAGES)
        .remove([filePath]);
      
      if (error) {
        console.error('Error deleting file:', error);
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Error in delete file function:', error);
      return false;
    }
  };

  const handleRemoveImage = async () => {
    if (!previewUrl) return;
    
    // If it's a URL from Supabase (not a local preview), delete the file
    if (previewUrl.includes('supabase')) {
      const success = await deleteFile(previewUrl);
      
      if (!success) {
        toast.error('Failed to remove image');
        return;
      }
      
      toast.success('Image removed successfully');
    }
    
    setPreviewUrl(null);
    onImageUploaded(''); // Clear the image URL in parent component
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-4">
        <input
          type="file"
          accept="image/jpeg, image/png"
          onChange={handleFileChange}
          ref={fileInputRef}
          className="hidden"
          disabled={uploading}
        />
        <Button 
          type="button" 
          onClick={handleBrowseClick}
          variant="outline"
          disabled={uploading}
          className="flex items-center space-x-2"
        >
          <Upload className="h-4 w-4" />
          <span>{uploading ? 'Uploading...' : 'Upload Profile Image'}</span>
        </Button>
        
        {previewUrl && (
          <Button
            type="button"
            onClick={handleRemoveImage}
            variant="ghost"
            size="sm"
            className="text-red-500 hover:text-red-700"
          >
            <X className="h-4 w-4 mr-1" />
            Remove
          </Button>
        )}
      </div>
      
      {previewUrl && (
        <div className="mt-2">
          <div className="relative w-24 h-24 rounded-md overflow-hidden border border-gray-200">
            <img 
              src={previewUrl} 
              alt="Profile preview" 
              className="w-full h-full object-cover"
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Max 500KB. JPEG or PNG only.
          </p>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
